import queryString from 'query-string';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import ImgixClient from '@imgix/js-core';

import { isBlank } from 'utils/helpers-ts';
import { ITransformationOptions } from 'utils/image-models';

const IMGIX_DOMAIN_REGEX = /(?:(?:https?:)?\/\/)?.*\.imgix\.net/i;
const IMGIX_CLOUD_NAME_REGEX = /(?:(?:https?:)?\/\/)?([^./]+)\.imgix\.net/i;

/**
 * Imgix auto transformation modes.
 *
 * - `enhance`: Enhances the image (contrast, saturation, brightness).
 * - `compress`: Applies best-effort compression.
 * - `format`: Automatically selects the best format (WebP, AVIF, etc.).
 * - `redeye`: Removes red eye from the image.
 * - `true`: Applies all default auto optimizations.
 */
const VALID_IMGIX_AUTO = ['enhance', 'compress', 'true', 'format', 'redeye'] as const;
type ImgixAuto = typeof VALID_IMGIX_AUTO[number];

/**
 * Imgix output formats.
 */
const VALID_IMGIX_FORMAT = [
  // images
  'avif',
  'gif',
  'jp2',
  'jpg',
  'jxr',
  'PDF',
  'pjpg',
  'png',
  'png8',
  'png32',
  'webp',
  // metadata
  'blurhash',
  'json',
  // video
  'mp4',
  'webm',
] as const;
type ImgixFormat = typeof VALID_IMGIX_FORMAT[number];

/**
 * Interface representing Imgix transformation parameters.
 *
 * @property w - Width in pixels
 * @property h - Height in pixels
 * @property dpi - Pixel density
 * @property auto - Auto transformation mode(s) (e.g., 'compress', 'enhance')
 * @property fm - Output format (e.g., 'webp', 'jpg')
 * @property bri - Brightness value (-100 to 100)
 */
export interface ImgixParams {
  w?: number | null;
  h?: number | null;
  dpi?: number | null;
  auto?: ImgixAuto | ImgixAuto[] | null;
  fm?: ImgixFormat | null;
  upscale?: string | number | boolean | null;
  /** Brightness value (-100 to 100) */
  bri?: number | null;
  /** Contrast value (-100 to 100) */
  con?: number | null;
  /** Saturation value (-100 to 100) */
  sat?: number | null;
  /** Sharpness value (0 to 100) */
  sharp?: number | null;
  /** Vibrance value (-100 to 100) */
  vib?: number | null;
}

/**
 * Helper to safely convert a string to a number.
 *
 * @param value - The string to parse
 * @returns The parsed number or null if invalid/empty
 */
const convertStringToNumber = (value?: string) => {
  if (isBlank(value)) return null;
  const parsed = parseInt(value!, 10);
  return !isNaN(parsed) ? parsed : null;
};

/**
 * Parses a value into a number using "last valid wins" logic for arrays.
 *
 * @param value - The value to parse (number, string, array, null, undefined)
 * @returns The parsed number or null
 */
const parseNumber = (value?: any): number | null => {
  if (isNil(value)) return null;

  if (typeof value === 'number') return value;
  if (typeof value === 'string') return convertStringToNumber(value);

  if (Array.isArray(value)) {
    for (let i = value.length - 1; i >= 0; i--) {
      const item = value[i];
      if (typeof item === 'number') return item;
      if (typeof item === 'string') {
        const parsed = convertStringToNumber(item);
        if (parsed !== null) return parsed;
      }
    }
  }

  return null;
};

/**
 * Checks if a URL is an Imgix URL.
 *
 * Checks if the URL contains `imgix.net`.
 *
 * @param url - The URL to check
 * @returns `true` if the URL is an Imgix URL, `false` otherwise
 *
 * @example
 * isImgix("https://my-source.imgix.net/image.jpg");
 * // Returns: true
 */
export const isImgix = (url?: string): url is string => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return false;

  return IMGIX_DOMAIN_REGEX.test(trimmedUrl!);
};

/**
 * Extracts the cloud name (subdomain) from an Imgix URL.
 *
 * @param url - The Imgix URL to parse
 * @returns The extracted cloud name, or `undefined` if not found
 *
 * @example
 * extractImgixCloudName("https://my-source.imgix.net/image.jpg");
 * // Returns: "my-source"
 */
export const extractImgixCloudName = (url?: string): string | undefined => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return undefined;

  const cloudNameMatch = trimmedUrl!.match(IMGIX_CLOUD_NAME_REGEX);
  return cloudNameMatch?.[1] || undefined;
};

/**
 * Extracts the public ID (path) from an Imgix URL.
 *
 * @param url - The Imgix URL to parse
 * @returns The extracted public ID, or `undefined` if not found
 *
 * @example
 * extractImgixPublicId("https://my-source.imgix.net/folder/image.jpg?w=100");
 * // Returns: "folder/image.jpg"
 */
export const extractImgixPublicId = (url?: string): string | undefined => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return undefined;

  if (!isImgix(trimmedUrl)) return undefined;

  const { url: rootUrl } = queryString.parseUrl(trimmedUrl!);

  // Find where the domain ends
  const domainMatch = rootUrl.match(IMGIX_DOMAIN_REGEX);
  if (!domainMatch) return undefined;

  const domainLength = domainMatch[0].length;
  let path = rootUrl.substring(domainLength);

  // Remove leading slash if present
  if (path.startsWith('/')) {
    path = path.substring(1);
  }

  return path || undefined;
};

/**
 * Extracts transformation options and valid query parameters from an Imgix URL.
 *
 * Extracts the width (`w`) and height (`h`) parameters into a transformation object,
 * and returns the remaining query parameters as a parsed object.
 *
 * @param url - The Imgix URL to parse
 * @returns An object containing the extracted transformation options and the remaining query parameters, or `undefined` if not found
 *
 * @example
 * extractImgixQueryString("https://my-source.imgix.net/image.jpg?w=100&auto=compress,enhance");
 * // Returns: { transform: { w: 100, auto: ['compress', 'enhance'] }, query: {} }
 */
export const extractImgixQueryString = (
  url?: string
): { transform: ImgixParams; query: queryString.ParsedQuery } | undefined => {
  const trimmedUrl = url?.trim();
  if (isBlank(trimmedUrl)) return undefined;

  if (!isImgix(trimmedUrl)) return undefined;

  const { query } = queryString.parseUrl(trimmedUrl!, { parseNumbers: true });

  const transform: ImgixParams = {};

  const width = parseNumber(query.w);
  if (width) transform.w = width;

  const height = parseNumber(query.h);
  if (height) transform.h = height;

  const density = parseNumber(query.dpi);
  if (density) transform.dpi = density;

  const autoParam = query.auto;
  const autoValue = Array.isArray(autoParam) ? autoParam : [autoParam];

  const autoResult: ImgixAuto[] = [];
  if (autoValue) {
    for (const auto of autoValue.flatMap(item => item?.split(','))) {
      const validAuto = auto?.trim() as ImgixAuto;
      if (VALID_IMGIX_AUTO.includes(validAuto)) {
        autoResult.push(validAuto);
      }
    }
  }
  if (autoResult.length === 1) {
    transform.auto = autoResult[0];
  } else if (autoResult.length > 1) {
    transform.auto = autoResult;
  }

  const formatParam = query.fm;
  const formatValue = Array.isArray(formatParam) ? formatParam[0] : formatParam;
  if (formatValue && VALID_IMGIX_FORMAT.includes(formatValue as ImgixFormat)) {
    transform.fm = formatValue as ImgixFormat;
  }

  const upscaleParam = query.upscale;
  if (!isNil(upscaleParam)) {
    transform.upscale = Array.isArray(upscaleParam) ? upscaleParam[0] : upscaleParam;
  }

  const bri = parseNumber(query.bri);
  if (bri) transform.bri = bri;

  const con = parseNumber(query.con);
  if (con) transform.con = con;

  const sat = parseNumber(query.sat);
  if (sat) transform.sat = sat;

  const sharp = parseNumber(query.sharp);
  if (sharp) transform.sharp = sharp;

  const vib = parseNumber(query.vib);
  if (vib) transform.vib = vib;

  const remainingQuery = { ...query };
  delete remainingQuery.w;
  delete remainingQuery.h;
  delete remainingQuery.dpi;
  delete remainingQuery.auto;
  delete remainingQuery.fm;
  delete remainingQuery.upscale;
  delete remainingQuery.bri;
  delete remainingQuery.con;
  delete remainingQuery.sat;
  delete remainingQuery.sharp;
  delete remainingQuery.vib;

  return { transform, query: remainingQuery };
};

/**
 * Builds Imgix transformation options from the provided options.
 *
 * Merges new transformation options with initial parameters.
 * - If an option is provided, it overrides the initial parameter.
 * - If an option is `null`, it removes the corresponding parameter from the result.
 * - If an option is `undefined`, the initial parameter is preserved.
 *
 * @param options - The transformation options (width, height, fetch_format, etc.)
 * @param initialParams - Optional initial Imgix parameters to merge with
 * @returns The resulting Imgix parameters
 *
 * @example
 * buildImgixTransformationOptions({ width: 100, fetch_format: 'webp' });
 * // Returns: { w: 100, fm: 'webp' }
 *
 * @example
 * buildImgixTransformationOptions({ width: 100 }, { h: 200 });
 * // Returns: { w: 100, h: 200 }
 *
 * @example
 * buildImgixTransformationOptions({ width: null }, { w: 100 });
 * // Returns: {} (w is removed)
 */
export const buildImgixTransformationOptions = (
  options?: ITransformationOptions,
  initialParams?: ImgixParams
): ImgixParams => {
  const params: ImgixParams = { ...initialParams };

  if (!options) return params;

  const applyOption = <K extends keyof ImgixParams>(key: K, value: ImgixParams[K], min?: number, max?: number) => {
    if (value === null) {
      delete params[key];
      return;
    }
    if (value === undefined) return;

    let finalValue = value;
    if (typeof finalValue === 'number') {
      if (min !== undefined && finalValue < min) finalValue = min as any;
      else if (max !== undefined && finalValue > max) finalValue = max as any;
    }

    params[key] = finalValue;
  };

  applyOption('w', options.width);
  applyOption('h', options.height);
  applyOption('dpi', options.density);

  applyOption('bri', options.brightness, -100, 100);
  applyOption('con', options.contrast, -100, 100);
  applyOption('sat', options.saturation, -100, 100);
  applyOption('sharp', options.sharpness, 0, 100);
  applyOption('vib', options.vibrance, -100, 100);

  let auto: ImgixAuto[] = [];
  if (!params.auto) {
    auto = [];
  } else if (Array.isArray(params.auto)) {
    auto = params.auto.filter(item => VALID_IMGIX_AUTO.includes(item));
  } else {
    if (VALID_IMGIX_AUTO.includes(params.auto)) {
      auto = [params.auto];
    }
  }

  switch (options.fetch_format) {
    case 'webp':
      params.fm = 'webp';
      break;
    case 'auto':
      delete params.fm;
      if (!auto.includes('format')) {
        auto.push('format');
      }
      break;
    case 'tiff':
      delete params.fm;
      console.error('tiff is not supported for imgix');
      break;
    default:
      delete params.fm;
      if (options.fetch_format) {
        console.error(`invalid fetch_format: ${options.fetch_format}`);
      }
      break;
  }

  if (options.compress && !auto.includes('compress')) {
    auto.push('compress');
  }

  if (auto.length > 0 && options.disableEnhance) {
    auto = auto.filter(item => item !== 'enhance');
  }

  if (auto.length === 1) {
    params.auto = auto[0];
  } else if (auto.length > 1) {
    params.auto = auto;
  }

  if (options.disableEnhance) {
    delete params.upscale;
  }

  if (options.upscale !== undefined) {
    if (options.upscale === null) {
      delete params.upscale;
    } else {
      params.upscale = options.upscale;
    }
  }

  return params;
};

/**
 * Transforms an Imgix URL by applying the specified transformation options.
 *
 * - Normalizes the URL (removes invalid parameters if options are provided).
 * - Preserves existing query parameters (e.g., `?foo=bar`).
 * - Merges new transformations with existing ones (overriding if necessary).
 * - Uses `ImgixClient` to build the URL and `query-string` to handle parameters.
 *
 * @param url - The original Imgix image URL (optional)
 * @param options - The transformation options (width, height, etc.)
 * @returns The transformed Imgix URL, or the original URL if it's not a valid Imgix URL
 *
 * @example
 * transformImgixImage("https://my-source.imgix.net/image.jpg", { width: 100 });
 * // Returns: "https://my-source.imgix.net/image.jpg?w=100"
 */
export const transformImgixImage = (url?: string, options?: ITransformationOptions): string => {
  if (isBlank(url)) return '';

  if (!options || isEmpty(options)) return url || '';

  if (!isImgix(url)) return url || '';

  const cloudName = extractImgixCloudName(url);
  if (isBlank(cloudName)) return url || '';

  const publicId = extractImgixPublicId(url);
  if (isBlank(publicId)) return url || '';

  const { transform, query } = extractImgixQueryString(url) || {};

  const finalParams = buildImgixTransformationOptions(options, transform);

  const client = new ImgixClient({
    domain: `${cloudName!}.imgix.net`,
    useHTTPS: true,
    includeLibraryParam: false,
  });

  const result = client.buildURL(publicId!, finalParams, { disablePathEncoding: true });

  if (!isEmpty(query)) {
    const { url, query: generatedParams } = queryString.parseUrl(result);
    const mergedQuery = { ...query, ...generatedParams };

    return queryString.stringifyUrl({ url: url!, query: mergedQuery }, { arrayFormat: 'comma' });
  }

  return result;
};

/**
 * Returns a transformed Imgix image URL with the specified dimensions.
 *
 * @param url - The original Imgix image URL (optional)
 * @param width - The target width in pixels (optional)
 * @param height - The target height in pixels (optional)
 * @returns The transformed URL with the specified dimensions, or empty string if url is empty
 *
 * @example
 * resizeImgix("https://my-source.imgix.net/image.jpg", 100, 200);
 * // Returns: "https://my-source.imgix.net/image.jpg?w=100&h=200"
 */
export const resizeImgix = (url?: string, width?: number, height?: number): string => {
  const resizeOptions: ITransformationOptions = {
    width: width,
    height: height,
  };

  if (width !== undefined && height === undefined) {
    resizeOptions.height = null;
  } else if (height !== undefined && width === undefined) {
    resizeOptions.width = null;
  }

  return transformImgixImage(url, resizeOptions);
};

/**
 * Returns a 64x64 transformed Imgix image URL.
 *
 * @param url - The original Imgix image URL (optional)
 * @returns The transformed URL with 64x64 dimensions, or empty string if url is empty
 */
export const resize64Imgix = (url?: string): string => resizeImgix(url, 64, 64);

/**
 * Returns a 128x128 transformed Imgix image URL.
 *
 * @param url - The original Imgix image URL (optional)
 * @returns The transformed URL with 128x128 dimensions, or empty string if url is empty
 */
export const resize128Imgix = (url?: string): string => resizeImgix(url, 128, 128);

/**
 * Returns a 256x256 transformed Imgix image URL.
 *
 * @param url - The original Imgix image URL (optional)
 * @returns The transformed URL with 256x256 dimensions, or empty string if url is empty
 */
export const resize256Imgix = (url?: string): string => resizeImgix(url, 256, 256);

/**
 * Returns a 512x512 transformed Imgix image URL.
 *
 * @param url - The original Imgix image URL (optional)
 * @returns The transformed URL with 512x512 dimensions, or empty string if url is empty
 */
export const resize512Imgix = (url?: string): string => resizeImgix(url, 512, 512);

/**
 * Removes width and height parameters from an Imgix URL.
 *
 * @param url - The original Imgix image URL (optional)
 * @returns The transformed URL without width/height parameters, or empty string if url is empty
 */
export const removeSizesImgix = (url?: string): string => transformImgixImage(url, { width: null, height: null });

/**
 * Adds pixel density (dpi) to an Imgix URL.
 *
 * @param url - The original Imgix image URL
 * @param density - The pixel density (e.g., 1, 2, 3)
 * @returns The transformed URL with the specified density
 */
export const addDensityImgix = (url?: string, density?: number): string => {
  return transformImgixImage(url, { density });
};

/**
 * Adds upscale parameter to an Imgix URL.
 *
 * @param url - The original Imgix image URL
 * @returns The transformed URL with upscale=true
 */
export const addUpscaleImgix = (url?: string): string => {
  return transformImgixImage(url, { upscale: true });
};

/**
 * Removes upscale parameter from an Imgix URL.
 *
 * @param url - The original Imgix image URL
 * @returns The transformed URL without upscale parameter
 */
export const removeUpscaleImgix = (url?: string): string => {
  return transformImgixImage(url, { upscale: null });
};

/**
 * Checks if an Imgix URL has the upscale parameter set to true.
 *
 * @param url - The Imgix image URL to check
 * @returns `true` if upscale=true, `false` otherwise
 */
export const isUpscaledImgix = (url?: string): boolean => {
  if (isBlank(url)) return false;
  if (!isImgix(url)) return false;

  const { transform } = extractImgixQueryString(url) || {};
  return transform?.upscale === 'true' || transform?.upscale === true;
};

/**
 * Metadata returned by the Imgix JSON API.
 *
 * @see https://docs.imgix.com/apis/rendering/format/fm#json
 */
export interface ImgixMetadata {
  /** Check EXIF data for the image. */
  Exif?: {
    ComponentsConfiguration?: string;
    PixelXDimension?: number;
    PixelYDimension?: number;
    FlashpixVersion?: string;
    ColorSpace?: number;
    ExifVersion?: string;
    [key: string]: any;
  };
  /** Orientation of the image. */
  Orientation?: number;
  /** Content type of the image (e.g., image/jpeg). */
  'Content-Type'?: string;
  /** Percentage of transparency in the image. */
  TransparentPercentage?: number;
  /** Output information. */
  Output?: Record<string, any>;
  /** PNG color type. */
  PngColorType?: number;
  /** Content length in bytes. */
  'Content-Length'?: string;
  /** DPI Width. */
  DPIWidth?: number;
  /** Pixel height of the image. */
  PixelHeight?: number;
  /** TIFF information. */
  TIFF?: {
    ResolutionUnit?: number;
    Orientation?: number;
    ExifTag?: number;
    XResolution?: number;
    YCbCrPositioning?: number;
    YResolution?: number;
    [key: string]: any;
  };
  /** GPS information. */
  GPS?: Record<string, any>;
  /** Pixel width of the image. */
  PixelWidth?: number;
  /** DPI Height. */
  DPIHeight?: number;
  /** Profile name. */
  ProfileName?: string;
  [key: string]: any;
}

/**
 * Fetches metadata for an Imgix image using the JSON API.
 *
 * It automatically removes any size parameters (w, h) and requests the `fm=json` format.
 *
 * @param url - The Imgix image URL (absolute)
 * @returns A promise that resolves to the image metadata, or `undefined` if the URL is invalid or the request fails
 */
export const getInfoImgix = async (url?: string): Promise<ImgixMetadata | undefined> => {
  try {
    const trimmedUrl = url?.trim();
    if (isBlank(trimmedUrl)) return undefined;

    if (!isImgix(trimmedUrl)) return undefined;

    const urlWithoutSizes = removeSizesImgix(trimmedUrl);
    if (isBlank(urlWithoutSizes)) return undefined;

    const infoUrl = queryString.stringifyUrl({ url: urlWithoutSizes, query: { fm: 'json' } });

    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(infoUrl, {
        signal: controller.signal,
      });
      clearTimeout(id);

      if (response.ok) {
        const json: ImgixMetadata = await response.json();
        return json;
      }
    } catch (err) {
      clearTimeout(id);
      throw err;
    }
  } catch (error) {
    console.error(error);
  }

  return undefined;
};
